<script setup>
// NOTICE: KAPTION-INTEGRATION
// Scheduled Messages modal: preview a scheduled WhatsApp template as the
// contact will receive it. The backend renders it into `template_preview`
// (KaptionScheduledMessagePreviewService, which reuses the renderer behind the
// sent-template bubble); the layout mirrors that bubble
// (components-next/message/bubbles/WhatsAppTemplate): header, body, footer and
// buttons. The template name stays visible as a caption.
//
// No preview means the template is not synced on the channel. Fall back to the
// text stored at scheduling time (message_content, written by the
// new-conversation modal), then to the values the agent typed, and only then
// to the bare template name, which is all the modal used to show.
import { computed } from 'vue';

const props = defineProps({
  message: {
    type: Object,
    required: true,
  },
});

const isFilled = value =>
  value !== undefined && value !== null && String(value).trim() !== '';

const preview = computed(() => props.message.template_preview || {});

const typedValues = computed(() =>
  Object.values(props.message.variables || {})
    .filter(isFilled)
    .map(String)
    .join(' · ')
);

const header = computed(() => preview.value.header || '');
const body = computed(
  () => preview.value.body || props.message.message_content || typedValues.value
);
const footer = computed(() => preview.value.footer || '');
const buttons = computed(() =>
  (preview.value.buttons || []).filter(button => isFilled(button?.text))
);
const hasText = computed(() => Boolean(header.value || body.value));

const BUTTON_ICONS = {
  url: 'i-lucide-external-link',
  phone_number: 'i-lucide-phone',
  copy_code: 'i-lucide-copy',
};
const buttonIcon = button =>
  BUTTON_ICONS[(button?.type || '').toLowerCase()] || 'i-lucide-reply';
</script>

<template>
  <div class="flex flex-col gap-1.5 min-w-0">
    <template v-if="hasText">
      <p
        v-if="header"
        class="mb-0 text-sm font-semibold text-n-slate-12 whitespace-pre-wrap break-words"
        data-test="template-header"
      >
        {{ header }}
      </p>
      <p
        v-if="body"
        class="mb-0 text-sm leading-relaxed text-n-slate-12 whitespace-pre-wrap break-words"
        data-test="template-body"
      >
        {{ body }}
      </p>
      <p
        v-if="footer"
        class="mb-0 text-xs text-n-slate-11 whitespace-pre-wrap break-words"
        data-test="template-footer"
      >
        {{ footer }}
      </p>
      <div v-if="buttons.length" class="flex flex-wrap gap-1.5 pt-0.5">
        <span
          v-for="(button, index) in buttons"
          :key="index"
          class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium border rounded-lg text-kpt-sky bg-kpt-sky/10 border-kpt-sky/30"
          data-test="template-button"
        >
          <span class="flex-shrink-0 size-3.5" :class="buttonIcon(button)" />
          {{ button.text }}
        </span>
      </div>
      <span
        class="flex items-center min-w-0 gap-1 text-xs text-n-slate-10"
        :title="$t('CONVERSATION.KAPTION_SCHEDULED_MESSAGES.TEMPLATE_MESSAGE')"
        data-test="template-name"
      >
        <span class="flex-shrink-0 i-lucide-file-text size-3" />
        <span class="truncate">{{ message.template_name }}</span>
      </span>
    </template>
    <p
      v-else
      class="mb-0 text-sm leading-relaxed text-n-slate-12 whitespace-pre-wrap break-words"
      data-test="template-name"
    >
      {{ message.template_name }}
    </p>
  </div>
</template>
